import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import styles from './UserMenu.module.css';
// 0.36.741 — Admin panel link now goes cross-domain to the admin
// subdomain when clicked from the public site; keeps admin routes
// off the public hostname per the "don't show /admin on public"
// separation.
import { adminPanelUrl, isAdminHost } from '../utils/adminHost';

// Inline icons — outlined style, currentColor, 1.75 stroke for a clean Henaket look.
const STROKE = 1.75;

const IconPerson = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth={STROKE} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="8" r="4" />
    <path d="M4 21c0-4 4-7 8-7s8 3 8 7" />
  </svg>
);

// Person inside a circle — used for "Admin Panel"
const IconUserCircle = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth={STROKE} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="10" />
    <circle cx="12" cy="10" r="3.2" />
    <path d="M5.5 18.5 C 7 15.5 9.5 14.2 12 14.2 C 14.5 14.2 17 15.5 18.5 18.5" />
  </svg>
);

// Document outline — used for pages in the user menu (0.36.153)
const IconDocument = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth={STROKE} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M14 3 H 7 a 2 2 0 0 0 -2 2 v 14 a 2 2 0 0 0 2 2 h 10 a 2 2 0 0 0 2 -2 V 8 Z" />
    <polyline points="14 3 14 8 19 8" />
  </svg>
);

// Logout — arrow exiting through a doorway, mirrored to point left like the screenshot
const IconLogout = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth={STROKE} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M15 21 h 4 a 2 2 0 0 0 2 -2 V 5 a 2 2 0 0 0 -2 -2 h -4" />
    <polyline points="8 17 3 12 8 7" />
    <line x1="3" y1="12" x2="15" y2="12" />
  </svg>
);

// 0.48.50 - The name, not the email: the account's first and last
// name, or the ID card's names in the page language. Wide screens show
// "N. Surname", narrow ones the initials "N.S.". No name: nothing.
function nameParts(user, language) {
  const first = (user.first_name || '').trim();
  const last = (user.last_name || '').trim();
  if (first || last) return { first, last };
  const hy = language === 'hy';
  return {
    first: ((hy ? user.cert_name_hy : user.cert_name_en) || '').trim(),
    last: ((hy ? user.cert_surname_hy : user.cert_surname_en) || '').trim(),
  };
}

const initial = (s) => (s ? Array.from(s)[0].toLocaleUpperCase() + '.' : '');

export function displayNames(user, language) {
  const { first, last } = nameParts(user, language);
  if (!first && !last) return null;
  return {
    wide: [initial(first), last].filter(Boolean).join(' ') || first,
    narrow: initial(first) + initial(last),
    full: [first, last].filter(Boolean).join(' '),
  };
}

function isAdmin(user) {
  return Array.isArray(user?.roles) && user.roles.includes('admin');
}

export default function UserMenu() {
  const { user, logout } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const handleEsc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEsc);
    };
  }, []);

  // 0.36.155 - pull "show_in_user_menu" pages. Called on mount AND on
  // every dropdown open, so that toggling the checkbox in the editor +
  // saving the page produces an updated dropdown without a hard refresh.


  if (!user) return null;

  const handleLogout = async () => {
    setOpen(false);
    try {
      await logout();
      navigate('/');
    } catch (e) {
      console.error('Logout failed', e);
    }
  };

  return (
    <div className={styles.wrapper} ref={wrapperRef}>
      <button
        type="button"
        className={styles.trigger}
        onClick={() => {
          setOpen(!open);
        }}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="User menu"
      >
        <span className={styles.avatar}>
          <IconPerson size={16} />
        </span>
        {(() => {
          const n = displayNames(user, language);
          return n ? (
            <span className={styles.triggerLabel} title={n.full}>
              <span className={styles.nameWide}>{n.wide}</span>
              <span className={styles.nameNarrow}>{n.narrow}</span>
            </span>
          ) : null;
        })()}
      </button>

      {open && (
        <div className={styles.menu} role="menu">
          <Link
            to="/profile"
            className={styles.menuItem}
            role="menuitem"
            onClick={() => setOpen(false)}
          >
            <span className={styles.menuIcon}><IconPerson /></span>
            <span className={styles.menuLabel}>{t('header.my_profile')}</span>
          </Link>

          {/* 0.44.06 - No "Show in user menu" pages: that setting is gone. */}

          {isAdmin(user) && (
            /* 0.36.741 — When on the public hostname, admin panel
             * link goes cross-domain to the admin hostname (0.44.89:
             * this installation's, from the public settings' admin_url)
             * (full-page navigation, not <Link>, so the browser hits
             * the admin subdomain and loads a fresh SPA there). On
             * the admin hostname the local <Link> continues to work
             * as before — no unnecessary page load. */
            isAdminHost() ? (
              <Link
                to="/admin"
                className={styles.menuItem}
                role="menuitem"
                onClick={() => setOpen(false)}
              >
                <span className={styles.menuIcon}><IconUserCircle /></span>
                <span className={styles.menuLabel}>{t('header.admin_panel')}</span>
              </Link>
            ) : (
              <a
                href={adminPanelUrl()}
                className={styles.menuItem}
                role="menuitem"
                onClick={() => setOpen(false)}
              >
                <span className={styles.menuIcon}><IconUserCircle /></span>
                <span className={styles.menuLabel}>{t('header.admin_panel')}</span>
              </a>
            )
          )}

          <div className={styles.menuDivider} />

          <button
            type="button"
            className={`${styles.menuItem} ${styles.logoutItem}`}
            role="menuitem"
            onClick={handleLogout}
          >
            <span className={styles.menuIcon}><IconLogout /></span>
            <span className={styles.menuLabel}>{t('header.logout')}</span>
          </button>
        </div>
      )}
    </div>
  );
}
